import {
	type QueryDslBoolQuery,
	type QueryDslQueryContainer,
} from '@elastic/elasticsearch/lib/api/types';

type BooleanClause = 'must' | 'must_not' | 'should' | 'filter';

/**
 * Normalizes a query or array of queries into an array
 */
export function normalizeQueryArray(
	query: QueryDslQueryContainer | QueryDslQueryContainer[] | undefined,
): QueryDslQueryContainer[] {
	if (!query) return [];
	return Array.isArray(query) ? query : [query];
}

/**
 * Merges multiple Elasticsearch bool queries together
 */
export function mergeBoolQueries(
	...boolQueries: QueryDslBoolQuery[]
): QueryDslBoolQuery {
	const result: QueryDslBoolQuery = {};

	// Handle all boolean clauses
	const clauses: BooleanClause[] = ['must', 'must_not', 'should', 'filter'];

	for (const clause of clauses) {
		const clauseQueries = boolQueries
			.map(query => query[clause])
			.filter(Boolean)
			.flatMap(normalizeQueryArray);

		if (clauseQueries.length > 0) {
			result[clause] = clauseQueries;
		}
	}

	// Handle minimum_should_match if present in any query
	const minShouldMatch = boolQueries
		.map(query => query.minimum_should_match)
		.filter(Boolean)
		.pop();

	if (minShouldMatch !== undefined) {
		result.minimum_should_match = minShouldMatch;
	}

	return result;
}

/**
 * Merges multiple Elasticsearch queries, handling both bool and non-bool queries
 */
export function mergeQueries(
	...queries: QueryDslQueryContainer[]
): QueryDslQueryContainer {
	// Filter out undefined/null queries
	const validQueries = queries.filter(Boolean);

	if (validQueries.length === 0) {
		return {};
	}

	if (validQueries.length === 1) {
		return validQueries[0];
	}

	// Convert all queries to bool queries
	const boolQueries = validQueries.map(query => {
		if (query.bool) {
			return query.bool;
		}
		// If it's not a bool query, wrap it in a must clause
		return { must: [query] };
	});

	const mergedBool = mergeBoolQueries(...boolQueries);
	return { bool: mergedBool };
}

/**
 * Type guard to check if a query container has a bool property
 */
export function isBoolQuery(
	query: QueryDslQueryContainer,
): query is QueryDslQueryContainer & { bool: QueryDslBoolQuery } {
	return 'bool' in query;
}

/**
 * Updates an existing search request with new query conditions
 */
export function updateSearchRequest(
	searchRequest: { body?: { query?: QueryDslQueryContainer } },
	newQuery: QueryDslQueryContainer,
): QueryDslQueryContainer {
	const existingQuery = searchRequest.body?.query;
	if (!existingQuery) {
		return newQuery;
	}

	return mergeQueries(existingQuery, newQuery);
}
